/*
 * Breadcrumbs
 * @module components
 * @flow
 */

import React, { cloneElement } from 'react';
import styled, { css } from 'styled-components/macro';
import { ifProp } from 'styled-tools';
import * as R from 'ramda';

// $FlowIssue
import { ReactComponent as IconHorizontalArrow } from '@/icons/ic-bc-arrow-h.svg';
// $FlowIssue
import { ReactComponent as IconVerticalArrow } from '@/icons/ic-bc-arrow-v.svg';

import { BreadcrumbsDropdown } from './components';

type BreadcrumbType = {
  value: string | number,
  label: string,
  level: number,
  onClick: () => void,
};

export type BreadcrumbsProps = {
  options: BreadcrumbType[],
};

const BreadcrumbsItem = styled.div.attrs(() => ({
  className: 'react-breadcrumbs-item',
}))`
  font-size: 16px;
  font-weight: 600;

  ${ifProp(
    { isClickable: true },
    css`
      color: #0d7cce;
      cursor: pointer;

      &:hover {
        text-decoration: underline;
      }
    `,
  )}
`;

const BreadcrumbsWrapper = styled.div.attrs(() => ({
  className: 'react-breadcrumbs',
}))`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  > ${BreadcrumbsItem} {
    line-height: 2;
  }
  z-index: 1;
`;

const BreadcrumbsHorizontalSeparator = styled(IconHorizontalArrow)`
  display: inline-flex;
  margin: 0 9px;
`;
const BreadcrumbsVerticalSeparator = styled(IconVerticalArrow)`
  margin: 6px 0;
`;

const renderItem = ({ label, onClick }: BreadcrumbType) => (
  <BreadcrumbsItem
    testid="breadcrumbsitem"
    key={`breadcrumbItem__${label}`}
    isClickable={!R.isNil(onClick)}
    onClick={onClick}
  >
    {label}
  </BreadcrumbsItem>
);

const renderDropdown = (list, rest) => (
  <BreadcrumbsDropdown key="breadcrumbsDropdown" {...rest}>
    {renderList(
      <BreadcrumbsVerticalSeparator key="BreadcrumbsVerticalSeparator" />,
    )(list)}
  </BreadcrumbsDropdown>
);

// $FlowIssue
const indexedReduce = R.addIndex(R.reduce);

const renderListItem = item => {
  if (!Array.isArray(item)) {
    // $FlowIssue
    return renderItem(item);
  }

  if (R.length(item) > 1) {
    return renderDropdown(item);
  }

  return renderItem(R.head(item));
};

const renderList = sep =>
  // $FlowIssue
  R.pipe(
    indexedReduce(
      (array, v, k) => [
        ...array,
        renderListItem(v),
        // $FlowIssue
        cloneElement(sep, { key: `${sep.key}__${k}` }),
      ],
      [],
    ),
    R.slice(0, -1),
  );

/**
 * Breadcrumbs React-component
 * @memberof components
 * @param {Object} props - properties
 * @param {BreadcrumbType[]} [props.options = []] - breadcrumbs items
 * @example <caption>Simple usage</caption>
 * <Breadcrumbs options={[{ value: 1, label: 'Adam Wong' }]} />
 * @returns {React.Node<*>} React-component
 */
export const Breadcrumbs = ({ options }: BreadcrumbsProps) => (
  <BreadcrumbsWrapper testid="breadcrumbs">
    {renderList(
      <BreadcrumbsHorizontalSeparator key="BreadcrumbsHorizontalSeparator" />,
    )(options)}
  </BreadcrumbsWrapper>
);

Breadcrumbs.defaultProps = {
  options: [],
};
